import { motion } from "framer-motion";
import Reveal from "@/components/Reveal";
import { fadeUp, viewport, stagger } from "@/lib/motion";
import { Quote, Linkedin } from "lucide-react";

const FOUNDERS = [
  {
    id: "gaurav",
    photo: "/dr-gaurav.jpg",
    name: "Dr. Gaurav Patel",
    credentials: "MBBS, MS, MPH (USA), MBA, FASTS (USA)",
    role: "Co-Founder, SuperAge Health™",
    subRole: "Clinical Director — Liver, Pancreas & Intestine Transplantation & HPB Surgery",
    linkedin: "https://www.linkedin.com/in/drgauravpatel",
    bio: "After performing more than 1,000 organ transplants, I realized that while transplantation saves lives, the greatest opportunity lies in preventing people from ever needing one. Every week, I care for patients with advanced liver disease that could have been detected years earlier through timely assessment and intervention. SuperAge Health was founded on a simple belief — that physician expertise, precision medicine, and technology can fundamentally transform liver health. By combining digital assessments, advanced diagnostics, and personalized clinical care, we aim to identify disease earlier, guide better decisions, and help people live healthier, longer lives.",
  },
  {
    id: "ripal",
    photo: "/dr-ripal.jpg",
    name: "Dr. Ripal Patel",
    credentials: "MBBS, PGDMLE, MBA",
    role: "Co-Founder, SuperAge Health™",
    subRole: "Director — Clinical Assessment & Precision Care",
    bio: "Healthcare should begin long before illness. At SuperAge Health, our vision is to make preventive healthcare simple, accessible, and actionable by combining digital innovation with physician expertise. We are building a platform that empowers individuals to understand their liver health through evidence-based risk assessment, precision diagnostics, personalized care pathways, and continuous health monitoring. Our goal is to transform healthcare from reactive treatment to proactive prevention, enabling measurable health outcomes that improve both the quality and longevity of life.",
  },
  // Hidden for now (reactivate later): Dr. Bhoomi Patel + Vaidehi Patel
  // {
  //   id: "bhoomi",
  //   photo: "/dr-bhoomi.jpg",
  //   name: "Dr. Bhoomi Patel",
  //   credentials: "BPT",
  //   role: "Director — Precision Nutrition & Lifestyle Medicine",
  //   bio: "An exercise physiotherapist, nutritionist, and certified lifestyle coach, she specializes in translating clinical and metabolic health data into personalized nutrition, movement, and behavior-change strategies. She works closely with the multidisciplinary SuperAge team to help individuals improve metabolic health, preserve muscle, reverse early fatty liver where appropriate, and build sustainable habits that support healthy aging.",
  // },
  // {
  //   id: "vaidehi",
  //   photo: "/vaidehi-patel.jpg",
  //   name: "Vaidehi Patel",
  //   credentials: "B.Com (Hons), MSc IBM (Finance & Marketing, London)",
  //   role: "Director — Growth & Business Development",
  //   bio: "Vaidehi Patel leads Growth & Business Development at SuperAge Health, driving strategic partnerships, brand growth, and commercial expansion. With a background in Finance and Marketing, she focuses on building scalable business models, strengthening collaborations, and creating meaningful patient experiences. She works across marketing, business development, and strategic initiatives to accelerate SuperAge Health's mission of making precision preventive healthcare more accessible, personalized, and impactful.",
  // },
];

const Portrait = ({ src, alt }) => (
  <div className="relative mx-auto w-full max-w-[260px] shrink-0 sm:max-w-[280px]">
    {/* soft copper glow behind portrait */}
    <div className="pointer-events-none absolute -inset-4 rounded-[50%] bg-sa-copper/10 blur-2xl" />
    <div className="relative aspect-[3/4] overflow-hidden rounded-[50%] bg-sa-beige/60 ring-1 ring-sa-beige">
      <img src={src} alt={alt} className="h-full w-full object-cover" />
    </div>
    {/* copper accent arc — decorative */}
    <span className="pointer-events-none absolute -bottom-2 left-1/2 h-2 w-16 -translate-x-1/2 rounded-full bg-sa-copper/40" />
  </div>
);

export const Founders = () => {
  return (
    <section id="founders" className="relative overflow-hidden bg-sa-bg py-12 md:py-16" data-testid="founders-section">
      <div className="mx-auto max-w-7xl px-6 md:px-12">
        <Reveal className="max-w-3xl">
          <p className="mb-4 text-[13px] font-semibold uppercase tracking-[0.2em] text-sa-copper">
            Our team
          </p>
          <h2 className="font-display text-4xl font-medium leading-[1.08] tracking-tight text-sa-forest sm:text-5xl">
            Meet the team.
          </h2>
          <p className="mt-5 text-lg leading-relaxed text-sa-muted">
            A physician-led team combining decades of clinical expertise, health-systems
            thinking, and technology to build India's precision liver-health platform.
          </p>
        </Reveal>

        <motion.div
          variants={stagger}
          initial="hidden"
          whileInView="show"
          viewport={viewport}
          className="mt-10 grid grid-cols-1 gap-8 md:mt-12 md:grid-cols-2 md:gap-10"
        >
          {FOUNDERS.map((f) => (
            <motion.article
              key={f.id}
              variants={fadeUp}
              data-testid={`founder-card-${f.id}`}
              className="flex flex-col gap-6 rounded-[32px] border border-sa-beige bg-white p-8 md:p-10"
            >
              <Portrait src={f.photo} alt={f.name} />
              <div>
                <h3 className="flex items-center gap-2 font-display text-2xl font-medium text-sa-forest sm:text-3xl">
                  <span>{f.name}</span>
                  {f.linkedin && (
                    <a
                      href={f.linkedin}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-[#0A66C2]/50 bg-[#0A66C2]/10 text-[#0A66C2] transition-all hover:border-[#0A66C2] hover:bg-[#0A66C2] hover:text-white"
                      data-testid={`founder-${f.id}-linkedin`}
                      aria-label={`${f.name} on LinkedIn`}
                      title="Connect on LinkedIn"
                    >
                      <Linkedin className="h-4 w-4" strokeWidth={2} fill="currentColor" />
                    </a>
                  )}
                </h3>
                <p className="mt-2 text-[13px] font-medium uppercase tracking-[0.14em] text-sa-copper">
                  {f.credentials}
                </p>
                <p className="mt-3 text-[15px] font-medium text-sa-forest">{f.role}</p>
                {f.subRole && (
                  <p className="mt-1 text-sm leading-relaxed text-sa-muted">{f.subRole}</p>
                )}
                <p className="mt-6 text-[15px] leading-relaxed text-sa-muted">
                  {f.bio}
                </p>
              </div>
            </motion.article>
          ))}
        </motion.div>

        {/* Our Vision */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={viewport}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="mt-12 grid grid-cols-1 gap-6 rounded-[32px] border border-sa-beige bg-white p-8 md:mt-12 md:grid-cols-12 md:gap-10 md:p-12"
          data-testid="founders-vision"
        >
          <div className="md:col-span-4">
            <p className="text-[13px] font-semibold uppercase tracking-[0.2em] text-sa-copper">
              Our Vision
            </p>
            <h3 className="mt-3 font-display text-3xl font-medium leading-[1.1] tracking-tight text-sa-forest sm:text-4xl">
              100 Million Livers<br />Assessed by 2035.
            </h3>
          </div>
          <p className="text-[15px] leading-relaxed text-sa-muted md:col-span-8 md:text-base">
            Through the Bharat Liver Mission, SuperAge Health is transforming liver care
            from reactive treatment to proactive prevention. By combining AI-powered risk
            assessment, physician expertise, precision diagnostics, and personalized
            interventions, we aim to make early liver health assessment accessible to every
            Indian — reducing the burden of advanced liver disease and preventing thousands
            of avoidable liver transplants.
          </p>
        </motion.div>

        {/* Our Purpose — pull quote */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={viewport}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="relative mt-10 overflow-hidden rounded-[32px] bg-sa-forest p-10 md:mt-12 md:p-16"
          data-testid="founders-purpose"
        >
          <div className="grain pointer-events-none absolute inset-0" />
          <div className="relative">
            <p className="text-[13px] font-semibold uppercase tracking-[0.2em] text-sa-copper">
              Our Purpose
            </p>
            <div className="relative mt-6 max-w-4xl">
              <Quote className="absolute -left-1 -top-4 h-10 w-10 text-sa-copper/30" strokeWidth={1.2} />
              <p className="font-display text-3xl font-medium leading-[1.15] tracking-tight text-white sm:text-4xl md:text-[2.75rem]">
                We don't aspire to perform more liver transplants. We aspire to ensure that
                fewer people ever need one.
              </p>
            </div>
            <p className="mt-8 text-sm font-medium uppercase tracking-[0.14em] text-sa-copper">
              — Dr. Gaurav Patel · Co-Founder, SuperAge Health™
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Founders;
